/**
 * hidden-focus-guard.js
 *
 * Site-wide accessibility fix: keyboard focus should never land on an
 * element that is visually hidden.
 * (Summer Furnishings — Ski Barn accessibility issue #44)
 *
 * Most of the theme's off-canvas panels (drawers, dropdowns) hide their
 * contents with `display:none` or `visibility:hidden`, which browsers
 * already exclude from the native Tab order automatically — no fix needed
 * there.
 *
 * A few controls, though — e.g. the mobile menu's fixed-position close
 * button (`.t4s-drawer-menu__close`, in snippets/render_bottom.liquid) —
 * animate in with `opacity:0; pointer-events:none` while closed instead of
 * `display:none`, so the browser still happily tabs a keyboard user
 * straight to an invisible, unusable control. That's the confirmed,
 * reproducible case behind this ticket, but the same CSS pattern could
 * exist (now or in the future) on any other element in the theme, so this
 * fix is generic rather than a one-off patch to a single button:
 *
 *   - Any focusable element that is effectively invisible (itself or an
 *     ancestor has opacity:0) gets tabindex="-1", pulling it out of the
 *     tab order while it's not usable.
 *   - When it becomes visible again (e.g. the drawer opens and the CSS
 *     sibling-selector flips opacity back to 1), its original tabindex is
 *     restored so it's reachable again.
 *   - Elements already hidden via display:none/visibility:hidden are left
 *     alone; the browser already keeps those out of the tab order, so
 *     touching their tabindex would be unnecessary churn.
 */
(function () {
  'use strict';

  var FOCUSABLE_SELECTOR =
    'a[href], button, input, select, textarea, [tabindex]';

  // Elements we've forced tabindex="-1" on, mapped to what tabindex (if
  // any) they had before, so it can be restored exactly once they're
  // visible again.
  var guarded = new WeakMap();

  function isOpacityZero(el) {
    var node = el;
    while (node && node.nodeType === 1) {
      var opacity = parseFloat(getComputedStyle(node).opacity);
      if (!isNaN(opacity) && opacity <= 0.01) return true;
      node = node.parentElement;
    }
    return false;
  }

  // Elements inside a display:none/visibility:hidden ancestor are already
  // excluded from the native tab order by the browser — leave them as-is.
  function isNativelyExcluded(el) {
    var node = el;
    while (node && node.nodeType === 1) {
      var style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return true;
      node = node.parentElement;
    }
    return false;
  }

  function sync() {
    var nodes = document.querySelectorAll(FOCUSABLE_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.disabled) continue;
      if (isNativelyExcluded(el)) continue;

      // Require pointer-events:none as well as a transparent ancestor. The
      // target case (.t4s-drawer-menu__close) is `opacity:0; pointer-events:none`,
      // but .t4s-product-wrapper sits permanently at opacity:0.01 while painted
      // and clickable -- opacity alone stripped every product card link out of
      // the tab order. pointer-events computes through ancestors, so this reads
      // the inherited value.
      var shouldGuard =
        isOpacityZero(el) && getComputedStyle(el).pointerEvents === 'none';
      var isGuarded = guarded.has(el);

      if (shouldGuard && !isGuarded) {
        guarded.set(el, el.getAttribute('tabindex'));
        el.setAttribute('tabindex', '-1');
      } else if (!shouldGuard && isGuarded) {
        var original = guarded.get(el);
        if (original === null) {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', original);
        }
        guarded.delete(el);
      }
    }
  }

  var scheduled = false;
  function scheduleSync() {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(function () {
      scheduled = false;
      sync();
    }, 50);
  }

  function init() {
    sync();

    var observer = new MutationObserver(scheduleSync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class', 'aria-hidden'],
      subtree: true,
      childList: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
